import React, { useState, useEffect } from "react";
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import styled from "styled-components";
import Button from "@mui/material/Button";
import { gql } from "@apollo/client";
import { client } from "../../../client";
import { uploadFileWithPresignedUrl } from "../../../utils/s3Uploader";
import FileUploader from "../admin-document/FileUploader";
import { TextField } from "@mui/material";

const ModalContainer = styled(Box)`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 80%;
  height: 80%;
  background-color: white;
  padding: 20px;
  z-index: 100;
  border: 1px solid #ccc;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
  overflow-y: auto; /* 스크롤 추가 */
`;

const UserInfoContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  align-items: center;
  margin-top: 30px;
  margin-bottom: 30px;
`;

const UserInfoItem = styled.div`
  display: flex;
  flex-direction: row;
  margin-bottom: 10px;
  width: 80%; /* 너비 조정 */
  align-items: flex-start; /* 상단 정렬 */
`;

const LabelBox = styled(Box)`
  font-weight: bold;
  width: 120px; /* 라벨 너비 고정 */
  margin-right: 10px;
  flex-shrink: 0;
`;

const ContentBox = styled(Box)`
  flex-grow: 1;
`;

const ReadOnlyTextField = styled(TextField)`
  .MuiInputBase-root {
    background-color: #f5f5f5;
  }
`;

const ModifyErrorAdmin = ({
  isOpen,
  data,
  handleCloseClick,
  refetchParentData,
}) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [editableData, setEditableData] = useState({ reply: "" });

  useEffect(() => {
    if (data) {
      setEditableData({
        id: data.id,
        user_id: data.user_id,
        type: data.type,
        title: data.title,
        content: data.content,
        object_url: data.object_url,
        reply: data.reply || "",
      });
    }
  }, [data]);

  const handleInputChange = (key, value) => {
    setEditableData((prevData) => ({
      ...prevData,
      [key]: value,
    }));
  };

  const handleRemoveImage = () => {
    if (window.confirm("첨부된 파일을 제거하시겠습니까?")) {
      setEditableData((prev) => ({ ...prev, object_url: null }));
      setSelectedFile(null);
    }
  };

  const handleSave = async () => {
    setIsLoading(true);
    try {
      let objectUrlToSubmit = editableData.object_url;

      if (selectedFile) {
        objectUrlToSubmit = await uploadFileWithPresignedUrl(
          client,
          selectedFile
        );
      }

      const MODIFY_ERROR_ADMIN = gql`
        mutation modifyErrorReport(
          $id: Int!
          $user_id: Int!
          $type: reportTypeEnum!
          $state: Int
          $title: String!
          $content: String
          $object_url: String
          $reply: String
        ) {
          modifyErrorReport(
            id: $id
            user_id: $user_id
            type: $type
            state: $state
            title: $title
            content: $content
            object_url: $object_url
            reply: $reply
          ) {
            message
          }
        }
      `;

      const variables = {
        id: editableData.id,
        user_id: editableData.user_id,
        type: editableData.type,
        state: 3, // 답변 완료 상태로 변경
        title: editableData.title,
        content: editableData.content,
        reply: editableData.reply,
        object_url: objectUrlToSubmit,
      };

      await client.mutate({
        mutation: MODIFY_ERROR_ADMIN,
        variables: variables,
      });

      alert("답변이 저장되었습니다.");
      refetchParentData();
      handleCloseClick();
    } catch (err) {
      alert(`수정 중 오류가 발생했습니다: ${err.message}`);
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const updatedTime = (params) => {
    if (!params) return "N/A";
    const date = new Date(parseInt(params));
    return date.toLocaleString("ko-kr");
  };

  if (!isOpen || !data) return null;

  return (
    <ModalContainer>
      <h2>오류 제보 답변하기</h2>
      <UserInfoContainer>
        <UserInfoItem>
          <LabelBox>ID:</LabelBox>
          <ContentBox>{data.id}</ContentBox>
        </UserInfoItem>
        <UserInfoItem>
          <LabelBox>유형:</LabelBox>
          <ContentBox>{data.type}</ContentBox>
        </UserInfoItem>
        <UserInfoItem>
          <LabelBox>제목:</LabelBox>
          <ContentBox>{data.title}</ContentBox>
        </UserInfoItem>
        <UserInfoItem>
          <LabelBox>내용:</LabelBox>
          <ContentBox>
            <ReadOnlyTextField
              fullWidth
              multiline
              rows={4}
              value={data.content || ""}
              InputProps={{ readOnly: true }}
            />
          </ContentBox>
        </UserInfoItem>
        <UserInfoItem>
          <LabelBox>첨부 파일:</LabelBox>
          <ContentBox>
            {editableData.object_url ? (
              <>
                <Box
                  component="img"
                  src={editableData.object_url}
                  sx={{
                    maxHeight: "200px",
                    maxWidth: "100%",
                    objectFit: "contain",
                    mb: 1,
                  }}
                />
                <Button
                  size="small"
                  color="error"
                  onClick={handleRemoveImage}
                  sx={{ display: "block", mb: 1 }}
                >
                  파일 제거
                </Button>
              </>
            ) : (
              <p>첨부된 파일이 없습니다.</p>
            )}
            <FileUploader
              selectedFiles={selectedFile ? [selectedFile] : []}
              setSelectedFiles={(files) => setSelectedFile(files[0] || null)}
            />
          </ContentBox>
        </UserInfoItem>
        <UserInfoItem>
          <LabelBox>답변:</LabelBox>
          <ContentBox>
            <TextField
              fullWidth
              multiline
              rows={6}
              placeholder="답변을 입력하세요..."
              value={editableData.reply}
              onChange={(e) => handleInputChange("reply", e.target.value)}
            />
          </ContentBox>
        </UserInfoItem>
        <UserInfoItem>
          <LabelBox>등록일:</LabelBox>
          <ContentBox>{updatedTime(data.created_at)}</ContentBox>
        </UserInfoItem>
      </UserInfoContainer>

      <Button variant="contained" onClick={handleSave} disabled={isLoading}>
        {isLoading ? "저장 중..." : "저장"}
      </Button>
      <Button onClick={handleCloseClick} sx={{ ml: 1 }}>
        닫기
      </Button>
    </ModalContainer>
  );
};

export default ModifyErrorAdmin;
